import { FetchResult } from '@apollo/client'
import cloneDeep from 'lodash/cloneDeep'
import compact from 'lodash/compact'
import first from 'lodash/first'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import join from 'lodash/join'
import map from 'lodash/map'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { ECommerceAddToCartItemProperties } from '@Analytics/ampli'
import { recoverableCartProducts } from '@Analytics/helpers'
import { useAmplitude } from '@Analytics/hook'
import { CartV2LineItem } from '@Domains/Cart/Feature/CartV2/CartV2'
import { AddToCartMutation, AddToCartProductInput, useAddToCartMutation } from '@Generated/graphql'
import { generateEventID, gtm } from '@Globals/Google/DataLayer'
import { lineItemToGA4Item, toMultiCurrency } from '@Globals/Google/ga4Helpers'
import { gtagEvent } from '@Globals/Google/GTag'
import { MetadataContext } from '@Globals/Metadata'
import { parseTacticList } from '@Globals/Tactic'

export type AddToCartFunc = (
  products: AddToCartProductInput[],
  config?: { skipReset?: boolean, sourceID?: string, withoutSuccessPrompt?: boolean }
) => Promise<FetchResult<AddToCartMutation> | null>
export type AddToCartFuncSync = (
  products: AddToCartProductInput[],
  config?: { skipReset?: boolean, sourceID?: string, withoutSuccessPrompt?: boolean }
) => void
interface UseAddToCartProps {
  sourceID?: string
  useCache?: boolean
  withoutSuccessPrompt?: boolean
}

const lineItemToAnalytics = (
  sourceID?: string,
  lineItem?: CartV2LineItem,
  isCrossSaleProductAdded?: boolean,
  personalizationLineItem?: CartV2LineItem,
): ECommerceAddToCartItemProperties | null => {
  if (!lineItem) {
    return null
  }
  const isPersonalisationAvailable = lineItem.variant?.personalizations?.__typename === 'ProductAttribute'
    ? !isEmpty(lineItem.variant?.personalizations.products)
    : false
  const personalizationProduct = lineItem.variant?.personalizations?.__typename === 'ProductAttribute'
    ? lineItem.variant?.personalizations.products?.find(p => p.id === personalizationLineItem?.productID)
    : undefined
  // @ts-ignore
  const personalizationType = personalizationProduct?.masterVariant?.type?.value?.key

  const categories: string[] = []

  const categoryMain = lineItem.product?.primaryCategories?.[0]?.name || ''
  if (categoryMain) {
    categories.push(categoryMain)
  }
  const categorySub = lineItem.product?.primaryCategories?.[1]?.name || ''
  if (categorySub) {
    categories.push(categorySub)
  }

  const type = lineItem.variant?.type?.__typename === 'BaseAttribute' ? lineItem.variant?.type?.value as { key?: string } : undefined
  const subType = lineItem.variant?.subType?.__typename === 'BaseAttribute' ? lineItem.variant?.subType?.value as { key?: string } : undefined
  const tactic = parseTacticList(lineItem.tactic)

  try {
    return ({
      attribute_sub_type: subType?.key || undefined,
      attribute_type: type?.key || undefined,
      brand: lineItem.product?.brand?.title || '',
      categories: join(categories, '/'),
      category_ids: `-${join(lineItem.product?.categoryIDs, '-')}-`,
      category_main: categoryMain,
      category_sub: categorySub,
      has_video: !!lineItem?.product?.assets?.find(a => a.tags?.find(t => t === 'video')),
      id: lineItem.productID || '',
      is_new: lineItem?.variant?.isNew && 'value' in lineItem.variant.isNew && lineItem.variant?.isNew.value === true,
      is_on_sale: (lineItem.price?.discount?.value || 0) > 0,
      // @ts-ignore
      local_price: (parseFloat(lineItem.totalTaxedPrice?.totalNet?.plain || '0') || 0) / (lineItem.quantity || 1),
      price: (parseFloat(lineItem.totalTaxedPrice?.totalNet?.base?.plain || '0') || 0) / (lineItem.quantity || 1),
      total_product_amount: parseFloat(lineItem.totalTaxedPrice?.totalNet?.base?.plain || '0') || 0,
      local_total_product_amount: parseFloat(lineItem.totalTaxedPrice?.totalNet?.plain || '0') || 0,
      quantity: lineItem.quantity,
      personalisation_type: personalizationType,
      has_personalised_products: !!personalizationLineItem,
      is_cross_sale_product_added: isCrossSaleProductAdded,
      // @ts-ignore
      is_high_cross_sale_available: (lineItem?.variant?.crossSells?.products || []).length !== 0,
      is_personalisation_available: isPersonalisationAvailable,
      personalisation_types: personalizationType,
      source_id: sourceID || 'inherit',
      product_type: lineItem.product?.type || 'unknown',
      product_id: lineItem.product?.id || '',
      variant_id: lineItem.variant?.id || '',
      milestone_id: lineItem.type?.milestoneID || undefined,
      applied_tactic_type: tactic?.type || undefined,
      applied_tactic_percentage: parseInt(tactic?.percentage || '', 10) || undefined,
    })
  } catch (e) {
    console.warn('LineItem Analytic data Generation ', e)
    return null
  }
}

export const useAddToCart = ({ sourceID, useCache = true, withoutSuccessPrompt = false }: UseAddToCartProps = {}) => {
  const [addToCartMutation, { data, loading: addToCartLoading, reset }] = useAddToCartMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })
  const { store } = useContext(MetadataContext)
  const ampli = useAmplitude({ sourceID })
  const [variables, setVariables] = useState<AddToCartProductInput[] | null>(null)
  const [cachedData, setCachedData] = useState(data)
  const [cartData, setCartData] = useState<NonNullable<NonNullable<typeof data>['addToCart']>['cart'] | undefined>(data?.addToCart?.cart)
  const prompts = useMemo(() => cachedData?.addToCart?.prompts || [], [cachedData?.addToCart?.prompts])
  useEffect(() => {
    setCartData(data?.addToCart?.cart)
    if (useCache && (!data || isEmpty(data.addToCart?.prompts))) {
      return
    }
    setCachedData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const wrapReset = useCallback(() => {
    setCachedData(null)
    reset()
  }, [reset, setCachedData])

  const isLoading = addToCartLoading
  const isDisabled = addToCartLoading || prompts.length !== 0
  const isSuccess = data?.addToCart?.success

  const addToCart: AddToCartFunc = async (products, config) => {
    if (isSuccess && !config?.skipReset) {
      wrapReset()
      return null
    }

    const input = cloneDeep(products)
    setVariables(input)

    try {
      const result = await addToCartMutation({
        context: {
          domainProxy: true,
          userSpecific: true,
        },
        variables: {
          products,
          withoutSuccessPrompt: withoutSuccessPrompt || !!config?.withoutSuccessPrompt,
        },
      })

      if (!result?.data?.addToCart?.success) {
        return result || null
      }
      ampli.cartAddClick({
        source_id: config?.sourceID || sourceID || 'inherit',
        quote_id: '',
        recoverable_cart_products: recoverableCartProducts(result.data.addToCart.cart),
      })
      gtm({
        event: 'addToCart',
      })
      const touchedLineIDs = result.data?.addToCart?.addedLineItemIDs || []
      const cartLineItems = result.data?.addToCart?.cart?.lineItems || []
      const touchedLines: CartV2LineItem[] = compact(touchedLineIDs.map(id => cartLineItems.find(li => li.id === id)))
      const addedItems = touchedLines.map((line) => {
        const inputForLine = products.find(p => p.productID === line.productID)
        const addedQty = inputForLine?.quantity ?? line.quantity ?? 1
        return { addedQty, line }
      })
      const currencyCode = store.config?.currencyCode
      const rates = store.config?.adwordsCurrencyRates
      const value = addedItems.reduce((sum, { addedQty, line }) => {
        const unitPrice = parseFloat(line.totalPrice?.plain || '0') / (line.quantity || 1)
        return sum + unitPrice * addedQty
      }, 0)
      gtagEvent('add_to_cart', {
        event_id: generateEventID(),
        currency: currencyCode,
        value,
        ...toMultiCurrency(value, currencyCode, rates),
        items: addedItems.map(({ addedQty, line }) => lineItemToGA4Item(line, currencyCode, rates, addedQty)),
      })
      const newCartData = result.data.addToCart.cart
      const useNewData = (result?.data?.addToCart?.prompts || []).find(p => p.__typename === 'AddToCartPromptSuccess' && 'primaryLineItemID' in p)
      const successData = useNewData ? result.data : cachedData
      const lineItemPrompt = (successData?.addToCart?.prompts || []).find(p => p.__typename === 'AddToCartPromptSuccess' && 'primaryLineItemID' in p)
      const usePrimaryLineItem = lineItemPrompt?.__typename === 'AddToCartPromptSuccess' && lineItemPrompt?.primaryLineItemID
      const useSecondaryLineItemIDs = lineItemPrompt?.__typename === 'AddToCartPromptSuccess' && lineItemPrompt?.secondaryLineItemIDs

      if (!config?.withoutSuccessPrompt && !usePrimaryLineItem && products.length === 1 && result.data.addToCart.addedLineItemIDs?.length === 1) {
        const foundPrimaryItem = result.data.addToCart.cart?.lineItems?.find(a => a.id === first(result.data?.addToCart?.addedLineItemIDs))
        if (foundPrimaryItem) {
          const analyticsData = lineItemToAnalytics(
            config?.sourceID || sourceID,
            foundPrimaryItem,
            false,
          )
          if (analyticsData) {
            ampli.eCommerceAddToCartItem(analyticsData)
          }
        }
      }

      if (!!config?.withoutSuccessPrompt || usePrimaryLineItem) {
        const successPromptOriginLineItem = newCartData?.lineItems?.find(li => (
          usePrimaryLineItem
            ? li.id === lineItemPrompt.primaryLineItemID
            : (li.product?.id === products[0].productID) || li.product?.id === products[0].variantID))
        const originLineItemCrossSellIDs = (successPromptOriginLineItem?.variant?.crossSells?.__typename === 'ProductAttribute'
            && successPromptOriginLineItem?.variant?.crossSells.products?.map(p => p.id)) || []
        const addedProductIDs: string[] = products.map(p => p.productID)
        const crossSells = newCartData?.lineItems?.filter(
          li => addedProductIDs.includes(li.productID || '')
              && originLineItemCrossSellIDs.includes(li.productID || '')
            && li.productID !== successPromptOriginLineItem?.productID
            && !li.type?.link.includes('personalization'),
        )
        const primaryPersonalizationLineItem = newCartData?.lineItems?.filter(
          li => addedProductIDs.includes(li.productID || '')
              && li.type?.link.includes('personalization')
            && li.type?.link.includes(successPromptOriginLineItem?.type?.link || ''),
        )?.[0]

        const analytic: any[] = []
        // ADDS PRIMARY PRODUCT
        if (addedProductIDs.includes(successPromptOriginLineItem?.product?.id || '')) {
          const primaryProductAnalytics = lineItemToAnalytics(
            config?.sourceID || sourceID,
            successPromptOriginLineItem,
            !isEmpty(crossSells),
            primaryPersonalizationLineItem,
          )

          if (primaryProductAnalytics && lineItemPrompt && lineItemPrompt.__typename === 'AddToCartPromptSuccess') {
            if (lineItemPrompt.tactic?.__typename === 'TacticMatchingVariant' && lineItemPrompt.tactic?.productID) {
              primaryProductAnalytics.tactic_type = 'matching_variant'
              primaryProductAnalytics.tactic_matching_variant_percentage = lineItemPrompt.tactic.offer?.price?.discount?.percent || 0
              primaryProductAnalytics.tactic_matching_variant_product_id = lineItemPrompt.tactic.productID
              // @ts-ignore .type is string, analytics expects literal union
              primaryProductAnalytics.tactic_matching_variant_type = lineItemPrompt.tactic?.type || undefined
            }
            if (lineItemPrompt.tactic?.__typename === 'TacticAddAndSave' && !isEmpty(lineItemPrompt.tactic.offers)) {
              primaryProductAnalytics.tactic_type = 'add_and_save'
              primaryProductAnalytics.tactic_add_and_save_product_ids = compact(lineItemPrompt.tactic.offers?.map(o => o.product?.id || o.productID))
              // @ts-ignore .type does not exist on TacticAddAndSave
              primaryProductAnalytics.tactic_matching_variant_type = lineItemPrompt.tactic?.type || undefined
            }
          }

          analytic.push(primaryProductAnalytics)
        }
        // ADDS SECONDARY PRODUCTS
        if (!isEmpty(useSecondaryLineItemIDs)) {
          forEach(useSecondaryLineItemIDs || [], (useSecondaryLineItemID) => {
            const secondaryLineItem = newCartData?.lineItems?.find(({ id }) => useSecondaryLineItemID === id)
            if (secondaryLineItem) {
              const secondaryProductAnalytics = lineItemToAnalytics(
                config?.sourceID || sourceID,
                secondaryLineItem,
                false,
              )
              analytic.push(secondaryProductAnalytics)
            }
          })
        }
        // ADDS PERSONALIZATION IN SUCCESS
        if (!addedProductIDs.includes(successPromptOriginLineItem?.product?.id || '') && primaryPersonalizationLineItem) {
          ampli.ppHcsPersonalisationAdded()
        }
        // ADDS RECOMMENDATIONS
        if (isEmpty(crossSells) && !addedProductIDs.includes(successPromptOriginLineItem?.product?.id || '') && !primaryPersonalizationLineItem) {
          const recommendationAdded = newCartData?.lineItems?.filter(li => addedProductIDs.includes(li.type?.link || ''))
          const recommendattionsAnaytics = map(
            recommendationAdded,
            (recomendation: CartV2LineItem) => lineItemToAnalytics('high_recommendation_sales', recomendation, false),
          )
          if (!isEmpty(recommendattionsAnaytics)) {
            analytic.push(...recommendattionsAnaytics)
          }
        }
        forEach(compact(analytic), (analyticData) => {
          // @ts-ignore
          ampli.eCommerceAddToCartItem(analyticData)
        })
      }

      return result || null
    } catch (error) {
      ampli.cartAddFailed({
        source_id: config?.sourceID || sourceID || 'inherit',
      })
    }

    return null
  }

  return [
    addToCart,
    {
      cartData,
      data: cachedData,
      isDisabled,
      isLoading,
      isSuccess,
      prompts,
      reset: wrapReset,
      variables,
    },
  ] as const
}
